import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { DateRange } from 'react-date-range';
import enUS from 'date-fns/locale/en-US';

import CustomSelect from '../../component/customSelect/customSelect';
import Header from '../../component/header/header';
import { status } from '../Templates/demoData';

import { getTemplates, publishTemplate, Template } from '../../api/template';
import {
  errorMessageShow,
  LoaderComponent,
  successMessage,
} from 'src/utils/Helper';
import { listSubject } from 'src/api/subject';
import { taxonomyItemNames } from 'src/utils/subjectTaxonomy';
import { ISubjects } from 'src/Context/TemplateData.context';
import SingleTemplate from './SingleTemplate';
import './Templates.style.scss';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
interface FilterOption {
  label: string;
  value: string;
}

const Templates = () => {
  const [filter, setFilter] = useState<{
    status: FilterOption | null;
    subject: FilterOption | null;
    publishedAt: FilterOption | null;
  }>({
    status: null,
    subject: null,
    publishedAt: null,
  });

  const [customDateRange, setCustomDateRange] = useState<{
    startDate: dayjs.Dayjs | null;
    endDate: dayjs.Dayjs | null;
  }>({ startDate: null, endDate: null });

  const [templateData, setTemplateData] = useState<Template[]>([]);
  const [subjects, setSubjects] = useState<ISubjects[]>([]);
  const [loading, setLoading] = useState(true);
  const [publishingId, setPublishingId] = useState<string | null>(null);
  const [openPopoverId, setOpenPopoverId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [showCustomRangePicker, setShowCustomRangePicker] = useState(false);

  const customRangeRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  const handleOnChange = (
    key: keyof typeof filter,
    option: FilterOption | null
  ) => {
    if (key === 'publishedAt' && option?.value === 'custom_range') {
      setShowCustomRangePicker(true);
      // do not set filter yet, wait for user to pick dates
    } else {
      setFilter(prev => ({ ...prev, [key]: option }));
      if (key === 'publishedAt') {
        setShowCustomRangePicker(false);
      }
    }
  };

  const handleClearFilters = () => {
    const isFilterActive =
      filter.status !== null ||
      filter.subject !== null ||
      filter.publishedAt !== null;

    if (isFilterActive) {
      setFilter({
        status: null,
        subject: null,
        publishedAt: null,
      });
      setCustomDateRange({ startDate: null, endDate: null });
      setShowCustomRangePicker(false);
    }
  };

  const handlePublish = async (templateId: string) => {
    setPublishingId(templateId);
    setOpenPopoverId(null);
    setError(null);
    try {
      const data = await publishTemplate(templateId);
      fetchTemplates();
      successMessage(data?.data?.message || 'Template Published Successfully.');
    } catch (error: any) {
      console.error('Error publishing template:', error);
      const errorMessage =
        error.response?.data?.detail || 'Failed to publish template.';
      setError(errorMessage);
      errorMessageShow(errorMessage);
    } finally {
      setPublishingId(null);
    }
  };

  useEffect(() => {
    fetchData();
  }, [filter]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        customRangeRef.current &&
        !customRangeRef.current.contains(event.target as Node)
      ) {
        setShowCustomRangePicker(false);
      }
    }
    if (showCustomRangePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showCustomRangePicker]);

  const fetchTemplates = async () => {
    try {
      setError(null);
      const params: Record<string, string> = {};

      if (filter.status && filter.status.value !== '') {
        params.status = filter.status.value.toLowerCase();
      }
      if (filter.subject && filter.subject.value !== '') {
        params.subject = filter.subject.value;
      }

      if (filter.publishedAt && filter.publishedAt.value !== '') {
        const now = dayjs();
        let startDate: dayjs.Dayjs | undefined;
        const endDate: dayjs.Dayjs = now;

        if (filter.publishedAt.value === 'custom_range') {
          if (customDateRange.startDate && customDateRange.endDate) {
            params.start_date = customDateRange.startDate
              .startOf('day')
              .toISOString();
            params.end_date = customDateRange.endDate
              .endOf('day')
              .toISOString();
          }
        } else {
          switch (filter.publishedAt.value) {
            case 'past_week':
              startDate = dayjs().subtract(7, 'day');
              break;
            case 'past_month':
              startDate = dayjs().subtract(1, 'month');
              break;
            case 'past_year':
              startDate = dayjs().subtract(1, 'year');
              break;
            default:
              break;
          }

          if (startDate) {
            params.start_date = startDate.toISOString();
            params.end_date = endDate.toISOString();
          }
        }
      }

      const res = await getTemplates(params);
      setTemplateData(res.data);
    } catch (error: any) {
      console.error('Error fetching templates:', error);
      setError(error.response?.data?.detail || 'Failed to fetch templates.');
    }
  };

  const fetchSubjects = async () => {
    try {
      const res = await listSubject(1, '', 100000);
      const apiSubjects = res.data?.data?.subjects ?? [];
      setSubjects(
        apiSubjects.map((s: any) => ({
          id: s.id,
          name: s.title || '',
          topics: taxonomyItemNames(s.topics || []),
        }))
      );
    } catch (error) {
      console.error('Failed to fetch subjects:', error);
      setSubjects([]);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
      await Promise.all([fetchTemplates(), fetchSubjects()]);
    } catch (error) {
      console.error('Error in one of the fetches:', error);
      setError('Failed to fetch data.');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    { label: 'All', value: '' },
    ...status.map(stat => ({ label: stat, value: stat })),
  ];

  const subjectOptions = [
    { label: 'All', value: '' },
    ...subjects.map(subject => ({
      label: subject.name,
      value: subject?.id.toString(),
    })),
  ];

  const getPublishedAtOptions = () => {
    const now = dayjs();
    const options = [{ label: 'All', value: '' }];

    const pastWeekStart = dayjs().subtract(7, 'day');
    options.push({
      label: `Past Week (${pastWeekStart.format('MMM D,YYYY')} - ${now.format('MMM D,YYYY')})`,
      value: 'past_week',
    });

    const pastMonthStart = dayjs().subtract(1, 'month');
    options.push({
      label: `Past Month (${pastMonthStart.format('MMM D,YYYY')} - ${now.format('MMM D,YYYY')})`,
      value: 'past_month',
    });

    const pastYearStart = dayjs().subtract(1, 'year');
    options.push({
      label: `Past Year (${pastYearStart.format('MMM D,YYYY')} - ${now.format('MMM D,YYYY')})`,
      value: 'past_year',
    });

    options.push({
      label: 'Custom Range',
      value: 'custom_range',
    });

    return options;
  };

  const publishedAtOptions = getPublishedAtOptions();

  return (
    <div className="content templates">
      <Header
        pageTitle="Templates"
        buttonText="Create Template"
        para={
          <span>
            <p>Ready-to-use structures for streamlined creation.</p>
          </span>
        }
        onClick={() => navigate('/templates/create')}
      />

      <div className="templates_container">
        <div className="template_filter">
          <div className="flex flex-wrap mt-3">
            <div className="w-auto pt-5">
              <p className="label">Filter</p>
            </div>

            <div className="w-full sm:w-[60%] md:w-[40%] lg:w-[30%] xl:w-[25%] p-2">
              <div className="status_filter">
                <CustomSelect
                  data={statusOptions}
                  value={filter.status}
                  onChange={(option: FilterOption | null) =>
                    handleOnChange('status', option)
                  }
                  placeholder="Status"
                />
              </div>
            </div>

            <div className="w-full sm:w-[60%] md:w-[40%] lg:w-[30%] xl:w-[25%] p-2">
              <div className="subject_filter">
                <CustomSelect
                  data={subjectOptions}
                  value={filter.subject}
                  onChange={(option: FilterOption | null) =>
                    handleOnChange('subject', option)
                  }
                  placeholder="Subject"
                />
              </div>
            </div>

            <div className="w-full sm:w-[60%] md:w-[40%] lg:w-[30%] xl:w-[25%] p-2 relative">
              <div className="published_at_filter">
                <CustomSelect
                  data={publishedAtOptions}
                  value={filter.publishedAt}
                  onChange={(option: FilterOption | null) =>
                    handleOnChange('publishedAt', option)
                  }
                  placeholder="Published AT"
                />
                {showCustomRangePicker && (
                  <div
                    ref={customRangeRef}
                    style={{
                      position: 'absolute',
                      zIndex: 9999,
                      background: 'white',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      borderRadius: 8,
                      marginTop: 8,
                      padding: '8px',
                    }}
                  >
                    <DateRange
                      ranges={[
                        {
                          startDate:
                            customDateRange.startDate?.toDate() || new Date(),
                          endDate:
                            customDateRange.endDate?.toDate() || new Date(),
                          key: 'selection',
                        },
                      ]}
                      onChange={(item: any) => {
                        const range = item.selection;
                        setCustomDateRange({
                          startDate: dayjs(range.startDate),
                          endDate: dayjs(range.endDate),
                        });
                      }}
                      maxDate={new Date()}
                      locale={enUS}
                    />
                    <div style={{ marginTop: 8, textAlign: 'right' }}>
                      <button
                        onClick={() => {
                          if (
                            customDateRange.startDate &&
                            customDateRange.endDate
                          ) {
                            const formattedLabel = `Custom Range (${customDateRange.startDate.format(
                              'MMM D, YYYY'
                            )} - ${customDateRange.endDate.format('MMM D, YYYY')})`;
                            setFilter(prev => ({
                              ...prev,
                              publishedAt: {
                                label: formattedLabel,
                                value: 'custom_range',
                              },
                            }));
                            setShowCustomRangePicker(false);
                          }
                        }}
                        className="px-3 py-1 bg-blue-600 text-white rounded"
                      >
                        Apply
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="w-full sm:w-auto p-2 flex items-center justify-center">
              <button
                onClick={handleClearFilters}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 transition-colors"
              >
                Reset
              </button>
            </div>
          </div>

          {error && (
            <div className="mt-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-md">
              {error}
            </div>
          )}

          <div className="templates_grid">
            {loading && templateData.length === 0 ? (
              <LoaderComponent fillPage label="Loading templates…" />
            ) : templateData && templateData.length > 0 ? (
              templateData.map(item => (
                <SingleTemplate
                  key={item._id}
                  item={item}
                  navigate={navigate}
                  publishingId={publishingId}
                  handlePublish={handlePublish}
                  openPopoverId={openPopoverId}
                  setOpenPopoverId={setOpenPopoverId}
                  subjects={subjects}
                />
              ))
            ) : (
              <p className="text-center w-full">No templates found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Templates;
